import type { ComponentType, SVGProps } from "react";
// Build-time Solar icons (via unplugin-icons), as in the mini app. This is the ONLY
// file that imports `~icons/solar/*`: call sites use `<Icon name=… />`.
import AddBold from "~icons/solar/add-circle-bold";
import AddLinear from "~icons/solar/add-circle-linear";
import CheckBold from "~icons/solar/check-circle-bold";
import CheckLinear from "~icons/solar/check-circle-linear";
import ChevronDownBold from "~icons/solar/alt-arrow-down-bold";
import ChevronDownLinear from "~icons/solar/alt-arrow-down-linear";
import ChevronLeftBold from "~icons/solar/alt-arrow-left-bold";
import ChevronLeftLinear from "~icons/solar/alt-arrow-left-linear";
import ChevronBold from "~icons/solar/alt-arrow-right-bold";
import ChevronLinear from "~icons/solar/alt-arrow-right-linear";
import ClockBold from "~icons/solar/clock-circle-bold";
import ClockLinear from "~icons/solar/clock-circle-linear";
import CloseBold from "~icons/solar/close-circle-bold";
import CloseLinear from "~icons/solar/close-circle-linear";
import CopyBold from "~icons/solar/copy-bold";
import CopyLinear from "~icons/solar/copy-linear";
import DangerBold from "~icons/solar/danger-triangle-bold";
import DangerLinear from "~icons/solar/danger-triangle-linear";
import DocumentBold from "~icons/solar/document-text-bold";
import DollarBold from "~icons/solar/dollar-minimalistic-bold";
import DollarLinear from "~icons/solar/dollar-minimalistic-linear";
import DocumentLinear from "~icons/solar/document-text-linear";
import DownloadBold from "~icons/solar/download-minimalistic-bold";
import DownloadLinear from "~icons/solar/download-minimalistic-linear";
import KeyBold from "~icons/solar/key-minimalistic-2-bold";
import KeyLinear from "~icons/solar/key-minimalistic-2-linear";
import ListBold from "~icons/solar/clipboard-list-bold";
import ListLinear from "~icons/solar/clipboard-list-linear";
import LogoutBold from "~icons/solar/logout-2-bold";
import LogoutLinear from "~icons/solar/logout-2-linear";
import MenuDotsBold from "~icons/solar/menu-dots-bold";
import MenuDotsLinear from "~icons/solar/menu-dots-linear";
import MonitorBold from "~icons/solar/monitor-bold";
import MonitorLinear from "~icons/solar/monitor-linear";
import MoonBold from "~icons/solar/moon-bold";
import MoonLinear from "~icons/solar/moon-linear";
import PauseBold from "~icons/solar/pause-circle-bold";
import PauseLinear from "~icons/solar/pause-circle-linear";
import PeopleBold from "~icons/solar/users-group-rounded-bold";
import PlayBold from "~icons/solar/play-circle-bold";
import PlayLinear from "~icons/solar/play-circle-linear";
import PeopleLinear from "~icons/solar/users-group-rounded-linear";
import RestartBold from "~icons/solar/restart-bold";
import RestartLinear from "~icons/solar/restart-linear";
import SearchBold from "~icons/solar/magnifer-bold";
import SearchLinear from "~icons/solar/magnifer-linear";
import SettingsBold from "~icons/solar/settings-bold";
import SettingsLinear from "~icons/solar/settings-linear";
import SimBold from "~icons/solar/sim-card-bold";
import SimLinear from "~icons/solar/sim-card-linear";
import CartBold from "~icons/solar/cart-large-2-bold";
import CartLinear from "~icons/solar/cart-large-2-linear";
import WalletBold from "~icons/solar/wallet-money-bold";
import WalletLinear from "~icons/solar/wallet-money-linear";
import StopBold from "~icons/solar/stop-circle-bold";
import StopLinear from "~icons/solar/stop-circle-linear";
import SunBold from "~icons/solar/sun-2-bold";
import SunLinear from "~icons/solar/sun-2-linear";
import TelegramBold from "~icons/solar/plain-bold";
import TelegramLinear from "~icons/solar/plain-linear";
import TrashBold from "~icons/solar/trash-bin-2-bold";
import TrashLinear from "~icons/solar/trash-bin-2-linear";
import UploadBold from "~icons/solar/upload-minimalistic-bold";
import UploadLinear from "~icons/solar/upload-minimalistic-linear";
import UserBold from "~icons/solar/user-circle-bold";
import UserLinear from "~icons/solar/user-circle-linear";

type SvgComp = ComponentType<SVGProps<SVGSVGElement>>;

/** Semantic names → a Solar Bold/Linear pair. "account" is a Telegram account. */
export const ICONS = {
  // Brand / navigation
  accounts: { Bold: TelegramBold, Linear: TelegramLinear },
  runs: { Bold: ListBold, Linear: ListLinear },
  people: { Bold: PeopleBold, Linear: PeopleLinear },
  settings: { Bold: SettingsBold, Linear: SettingsLinear },
  sim: { Bold: SimBold, Linear: SimLinear },
  cart: { Bold: CartBold, Linear: CartLinear },
  wallet: { Bold: WalletBold, Linear: WalletLinear },
  user: { Bold: UserBold, Linear: UserLinear },
  logout: { Bold: LogoutBold, Linear: LogoutLinear },

  // Actions
  add: { Bold: AddBold, Linear: AddLinear },
  retry: { Bold: RestartBold, Linear: RestartLinear },
  pause: { Bold: PauseBold, Linear: PauseLinear },
  play: { Bold: PlayBold, Linear: PlayLinear },
  stop: { Bold: StopBold, Linear: StopLinear },
  trash: { Bold: TrashBold, Linear: TrashLinear },
  download: { Bold: DownloadBold, Linear: DownloadLinear },
  upload: { Bold: UploadBold, Linear: UploadLinear },
  copy: { Bold: CopyBold, Linear: CopyLinear },
  search: { Bold: SearchBold, Linear: SearchLinear },
  more: { Bold: MenuDotsBold, Linear: MenuDotsLinear },
  document: { Bold: DocumentBold, Linear: DocumentLinear },
  key: { Bold: KeyBold, Linear: KeyLinear },
  chevronRight: { Bold: ChevronBold, Linear: ChevronLinear },
  chevronLeft: { Bold: ChevronLeftBold, Linear: ChevronLeftLinear },
  chevronDown: { Bold: ChevronDownBold, Linear: ChevronDownLinear },
  close: { Bold: CloseBold, Linear: CloseLinear },

  // Status
  check: { Bold: CheckBold, Linear: CheckLinear },
  clock: { Bold: ClockBold, Linear: ClockLinear },
  warning: { Bold: DangerBold, Linear: DangerLinear },

  // Money
  price: { Bold: DollarBold, Linear: DollarLinear },

  // Theme
  sun: { Bold: SunBold, Linear: SunLinear },
  moon: { Bold: MoonBold, Linear: MoonLinear },
  system: { Bold: MonitorBold, Linear: MonitorLinear },
} satisfies Record<string, { Bold: SvgComp; Linear: SvgComp }>;

export type IconName = keyof typeof ICONS;
export type IconVariant = "Bold" | "Linear";
